/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import { Modal, Button, message, Typography } from 'antd';
import { useState, useEffect, useMemo, useRef } from 'react';
import { useIntl } from 'react-intl';
import { ModalTitle } from '~components/_common/ModalTitle/ModalTitle';
import { ThemedStyleFn, useStyles } from '~src/hooks/useStyles';
import { DraggableTableRow } from './components/DraggableTableRow';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { dropTargetForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';

const { Text } = Typography;

export interface ReorganizeFoldersModalProps {
  visible: boolean;
  onCancel: () => void;
  folders: any[];
  onSave: (reorganizedFolders: any[]) => void;
}

export const ReorganizeFoldersModal: React.FC<ReorganizeFoldersModalProps> = ({
  visible,
  onCancel,
  folders,
  onSave,
}) => {
  const intl = useIntl();
  const styles = useStyles(styleFn);
  const [localFolders, setLocalFolders] = useState<any[]>([]);
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const rootDropZoneRef = useRef<HTMLDivElement>(null);
  const [isRootDropTarget, setIsRootDropTarget] = useState(false);

  useEffect(() => {
    if (visible) {
      setLocalFolders([...folders]);
    }
  }, [visible, folders]);

  // Root drop zone drag and drop logic
  useEffect(() => {
    const el = rootDropZoneRef.current;
    if (!el) return;

    return dropTargetForElements({
      element: el,
      onDragEnter: () => setIsRootDropTarget(true),
      onDragLeave: () => setIsRootDropTarget(false),
      onDrop: ({ source }) => {
        setIsRootDropTarget(false);
        const folderId = source.data.folderId as number;
        handleMakeRoot(folderId);
      },
    });
  }, [localFolders]);

  // Auto-scroll logic for smooth scrolling during drag
  useEffect(() => {
    let rafId: number;
    let isScrolling = false;
    let lastClientY = 0;

    const handleDragMove = (e: DragEvent) => {
      if (!tableContainerRef.current) return;

      lastClientY = e.clientY;
      const container = tableContainerRef.current;
      const { top, bottom } = container.getBoundingClientRect();
      const scrollZone = 60;
      const scrollSpeed = 8;

      const autoScroll = () => {
        if (!isScrolling || !tableContainerRef.current) return;

        const container = tableContainerRef.current;

        if (lastClientY - top < scrollZone && container.scrollTop > 0) {
          container.scrollTop -= scrollSpeed;
          rafId = requestAnimationFrame(autoScroll);
        } else if (bottom - lastClientY < scrollZone) {
          const maxScroll = container.scrollHeight - container.clientHeight;
          if (container.scrollTop < maxScroll) {
            container.scrollTop += scrollSpeed;
            rafId = requestAnimationFrame(autoScroll);
          }
        } else {
          isScrolling = false;
        }
      };

      if (lastClientY - top < scrollZone || bottom - lastClientY < scrollZone) {
        if (!isScrolling) {
          isScrolling = true;
          autoScroll();
        }
      } else {
        isScrolling = false;
        if (rafId) cancelAnimationFrame(rafId);
      }
    };

    const handleDragEnd = () => {
      isScrolling = false;
      if (rafId) cancelAnimationFrame(rafId);
    };

    document.addEventListener('dragover', handleDragMove);
    document.addEventListener('dragend', handleDragEnd);
    document.addEventListener('drop', handleDragEnd);

    return () => {
      document.removeEventListener('dragover', handleDragMove);
      document.removeEventListener('dragend', handleDragEnd);
      document.removeEventListener('drop', handleDragEnd);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, []);

  // Build hierarchical structure with children
  const folderTree = useMemo(() => {
    const buildTree = (parentId: number | null): any[] => {
      return localFolders
        .filter((f) => f.parent_id === parentId)
        .map((folder) => ({
          ...folder,
          children: buildTree(folder.id),
        }));
    };
    return buildTree(null);
  }, [localFolders]);

  // Helper function to detect circular dependencies
  const wouldCreateCircle = (
    draggedId: number,
    targetId: number,
    folderList: any[]
  ): boolean => {
    // Check if targetId is a descendant of draggedId
    const isDescendant = (parentId: number, childId: number): boolean => {
      const folder = folderList.find((f) => f.id === childId);
      if (!folder || !folder.parent_id) return false;
      if (folder.parent_id === parentId) return true;
      return isDescendant(parentId, folder.parent_id);
    };
    return isDescendant(draggedId, targetId);
  };

  const handleNest = (draggedId: number, targetId: number) => {
    // Prevent circular dependency
    if (wouldCreateCircle(draggedId, targetId, localFolders)) {
      message.warning(
        intl.formatMessage({ id: 'circular-dependency-warning' })
      );
      return;
    }

    // Prevent self-nesting
    if (draggedId === targetId) {
      return;
    }

    const updatedFolders = localFolders.map((folder) => {
      if (folder.id === draggedId) {
        return { ...folder, parent_id: targetId };
      }
      return folder;
    });
    setLocalFolders(updatedFolders);
  };

  const handleMakeRoot = (folderId: number) => {
    const updatedFolders = localFolders.map((folder) => {
      if (folder.id === folderId) {
        return { ...folder, parent_id: null };
      }
      return folder;
    });
    setLocalFolders(updatedFolders);
  };

  const handleRemove = (folderId: number) => {
    const updatedFolders = localFolders.filter(
      (folder) => folder.id !== folderId
    );
    setLocalFolders(updatedFolders);
  };

  const handleSave = () => {
    onSave(localFolders);
    onCancel();
  };

  const handleCancel = () => {
    setLocalFolders([...folders]);
    onCancel();
  };

  return (
    <Modal
      title={
        <ModalTitle
          text={intl.formatMessage({ id: 'reorganize-folders' })}
          icon={['far', 'folder-tree']}
        />
      }
      visible={visible}
      onCancel={handleCancel}
      width={1000}
      centered
      maskClosable={false}
      footer={
        <div css={styles.footer}>
          <Button onClick={handleCancel}>
            {intl.formatMessage({ id: 'cancel' })}
          </Button>
          <Button type="primary" onClick={handleSave}>
            {intl.formatMessage({ id: 'save' })}
          </Button>
        </div>
      }>
      <div css={styles.content}>
        <div css={styles.helpText}>
          <FontAwesomeIcon icon={['fas', 'info-circle']} />
          <Text type="secondary">
            {intl.formatMessage({ id: 'drag-to-nest' })}
          </Text>
        </div>
        <div
          ref={rootDropZoneRef}
          css={[
            styles.rootDropZone,
            isRootDropTarget && styles.rootDropZoneActive,
          ]}>
          <FontAwesomeIcon icon={['fas', 'level-up-alt']} />
          <Text>Drag folders here to make them root level</Text>
        </div>
        {folderTree.length === 0 ? (
          <div css={styles.emptyState}>
            <FontAwesomeIcon icon={['fad', 'folder-open']} />
            <div>{intl.formatMessage({ id: 'no-folders-to-reorganize' })}</div>
          </div>
        ) : (
          <div ref={tableContainerRef} css={styles.tableContainer}>
            <table css={styles.table}>
              <thead>
                <tr>
                  <th style={{ width: '45%' }}>
                    {intl.formatMessage({ id: 'folder-name' })}
                  </th>
                  <th style={{ width: '45%' }}>
                    {intl.formatMessage({ id: 'description' })}
                  </th>
                  <th style={{ width: '10%', textAlign: 'center' }}>
                    {intl.formatMessage({ id: 'actions' })}
                  </th>
                </tr>
              </thead>
              <tbody>
                {folderTree.map((folder, index) => (
                  <DraggableTableRow
                    key={folder.id}
                    folder={folder}
                    level={0}
                    isLastChild={index === folderTree.length - 1}
                    parentChain={[]}
                    onNest={handleNest}
                    onRemove={handleRemove}
                    canNest={(draggedId, targetId) => {
                      return (
                        !wouldCreateCircle(draggedId, targetId, localFolders) &&
                        draggedId !== targetId
                      );
                    }}
                    children={folder.children}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Modal>
  );
};

const styleFn: ThemedStyleFn = (theme) => ({
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
  },
  helpText: {
    padding: '12px 16px',
    backgroundColor: theme.colors.primaryLight || '#f0f5ff',
    borderRadius: 6,
    border: `1px solid ${theme.colors.primary}`,
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    color: theme.colors.text.primary,
    '& svg': {
      fontSize: 16,
      color: theme.colors.primary,
    },
  },
  rootDropZone: {
    padding: '24px 16px',
    backgroundColor: theme.colors.backgroundSecondary || '#fafafa',
    borderRadius: 6,
    border: `2px dashed ${theme.colors.border || '#d9d9d9'}`,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 12,
    transition: 'all 0.2s ease',
    cursor: 'default',
    '& svg': {
      fontSize: 28,
      color: theme.colors.textSecondary || '#8c8c8c',
      transition: 'all 0.2s ease',
    },
    '& span': {
      color: theme.colors.textSecondary || '#8c8c8c',
      fontSize: 14,
      fontWeight: 500,
      transition: 'all 0.2s ease',
    },
  },
  rootDropZoneActive: {
    backgroundColor: '#f6ffed',
    borderColor: theme.colors.success || '#52c41a',
    borderStyle: 'solid',
    '& svg': {
      color: theme.colors.success || '#52c41a',
      transform: 'scale(1.1)',
    },
    '& span': {
      color: theme.colors.success || '#52c41a',
    },
  },
  tableContainer: {
    maxHeight: '500px',
    overflow: 'auto',
    border: `1px solid ${theme.colors.border}`,
    borderRadius: 8,
    position: 'relative',

    '&::-webkit-scrollbar': {
      width: 8,
      height: 8,
    },
    '&::-webkit-scrollbar-track': {
      background: theme.colors.backgroundSecondary || '#f5f5f5',
      borderRadius: 4,
    },
    '&::-webkit-scrollbar-thumb': {
      background: theme.colors.border || '#d9d9d9',
      borderRadius: 4,
      '&:hover': {
        background: theme.colors.textSecondary || '#bfbfbf',
      },
    },
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: 14,

    '& thead': {
      position: 'sticky',
      top: 0,
      zIndex: 10,
      backgroundColor: theme.colors.backgroundSecondary || '#fafafa',
    },
    '& th': {
      backgroundColor: theme.colors.backgroundSecondary || '#fafafa',
      fontWeight: 600,
      borderBottom: `2px solid ${theme.colors.border}`,
      padding: '12px 16px',
      textAlign: 'left',
      color: theme.colors.text.primary,
    },
    '& td': {
      padding: '0',
      borderBottom: `1px solid ${theme.colors.border}`,
      height: 48,
      '&:first-of-type': {
        paddingLeft: 16,
      },
      '&:nth-of-type(2)': {
        padding: '12px 16px',
      },
      '&:last-of-type': {
        padding: '12px 16px',
      },
    },
    '& .draggable-row': {
      transition: 'all 0.2s ease',
      cursor: 'grab',
      '&:hover': {
        backgroundColor: theme.colors.primaryLight || '#f0f5ff',
        '& .drag-handle': {
          opacity: 1,
        },
      },
      '&.dragging': {
        opacity: 0.5,
        cursor: 'grabbing',
        backgroundColor: theme.colors.backgroundSecondary || '#f5f5f5',
      },
      '&.drop-target': {
        backgroundColor: '#f6ffed',
        '& > td:first-child': {
          borderLeft: `3px solid ${theme.colors.success || '#52c41a'}`,
        },
        boxShadow: 'inset 0 0 0 1px rgba(82, 196, 26, 0.2)',
      },
    },
    '& .drag-handle': {
      opacity: 0.3,
      transition: 'opacity 0.2s',
      cursor: 'grab',
      fontSize: 12,
      color: theme.colors.textSecondary,
    },
  },
  emptyState: {
    padding: '60px 20px',
    textAlign: 'center',
    color: theme.colors.textSecondary,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 16,
    '& svg': {
      fontSize: 48,
      opacity: 0.3,
    },
  },
  footer: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 8,
    padding: '16px 0 0 0',
    borderTop: `1px solid ${theme.colors.border}`,
    marginTop: 16,
  },
});
